import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closeEditAttachment } from 'reducers/DialogsReducer';
import Dialog from 'components/Dialog';
import AttachmentIcon from 'components/AttachmentIcon';
import Checkbox2 from 'components/Checkbox2';
import TextField2 from 'components/TextField2';
import { useError } from 'utils/formStyles';
import {
  updateAttachment,
  downloadBufferAttachment,
  getFileURLFromBuffer,
  getAttachmentById,
} from 'reducers/FileReducer';
import FormSpinner from 'components/FormSpinner';
import { Fab, Tooltip } from '@material-ui/core';
import DownloadIcon from '@material-ui/icons/GetApp';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { handleError } from 'reducers/ErrorReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';

const useStyles = makeStyles((theme) => ({
  imageDialogLayout: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  imagePreviewPane: {
    flex: '0 0 40%',
    display: 'flex',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1),
    maxHeight: 400,
    overflow: 'hidden',
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: 380,
    objectFit: 'contain',
  },
  detailsPane: {
    flex: '1 1 60%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0, // prevents flex child from overflowing on long text/fields
  },
  fileInfo: {
    minHeight: 35,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    '& img': {
      maxHeight: 24,
      borderRadius: 3,
      marginRight: 8,
    },
  },
  g: {
    minWidth: 350, // Only for non-preview
  },
  txtField: {
    width: '100%', // adjust to your existing non-image field width if different
  },
}));

function EditAttachment(props) {
  const classes = useStyles();
  const { FileType, dataURL, ptsAttachmentID } = props.data;
  const [FileName, setFileName] = useState('');
  const [Description, setDescription] = useState('');
  const [IsDeleted, setIsDeleted] = useState(false);
  const [errors, setErrors] = useState();
  const { err, isValidAndChanged, formChanged } = useError(errors, setErrors);
  const [attachmentBuffer, setAttachmentBuffer] = useState(dataURL ?? null);
  const [loading, setLoading] = useState(false);
  const IMAGE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'tiff',
    'tif',
    'ico',
    'heic',
    'heif',
  ];
  const isImage = IMAGE_EXTENSIONS.includes(FileType);

  useEffect(() => {
    setLoading(true);
    const { Description, IsDeleted, FileName } = props.data;
    setDescription(Description);
    setIsDeleted(Boolean(IsDeleted));
    setFileName(FileName.substr(0, FileName.length - FileType.length - 1));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!attachmentBuffer) {
      getAttachmentById(ptsAttachmentID)
        .then((result) => {
          setAttachmentBuffer(result?.FileObject ?? result?.buffer ?? null);
          setLoading(false);
        })
        .catch(() => {
          props.handleError();
          setLoading(false);
        });
    } else setLoading(false);
  }, [attachmentBuffer, ptsAttachmentID]);

  const close = () => {
    props.closeEditAttachment();
  };

  const OnDownloadClick = () => downloadBufferAttachment(attachmentBuffer, FileType, FileName);

  const save = () => {
    const type = props.data.ParentType;
    const data = {
      ptsAttachmentID: props.data.ptsAttachmentID,
      FileName: `${FileName}.${FileType}`,
      Description,
      IsDeleted: IsDeleted ? 1 : 0,
    };
    props.showSpinner();
    updateAttachment(data)
      .then(() => {
        props.notifyDataUpdate({ type: 'file-upload', data: type });
        props.closeEditAttachment();
      })
      .catch(props.handleError)
      .finally(props.hideSpinner);
  };

  const renderActions = () => {
    return (
      <>
        {attachmentBuffer && (
          <Tooltip title="Download">
            <Fab size="small" color="secondary" aria-label="citation download">
              <DownloadIcon onClick={OnDownloadClick} style={{ margin: '0px' }} />
            </Fab>
          </Tooltip>
        )}
        <Button
          color="primary"
          variant="contained"
          autoFocus
          onClick={save}
          disabled={!isValidAndChanged()}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const renderFileName = () => {
    const onChange = (ev, val) => {
      setFileName(val);
      formChanged();
    };
    const error = err(FileName, 'FileName', 'File name cannot be empty');
    return (
      <div>
        <TextField2
          label="File Name"
          value={FileName}
          onChange={onChange}
          className={classes.txtField}
          error={error}
          max={255}
        />
      </div>
    );
  };

  const renderDescription = () => {
    const onChange = (ev, val) => {
      setDescription(val);
      formChanged();
    };
    return (
      <div>
        <TextField2
          label="Description"
          value={Description}
          onChange={onChange}
          className={classes.txtField}
          compact="true"
          max={50}
        />
      </div>
    );
  };

  const renderIsDeleted = () => {
    const onChange = (ev, val) => {
      setIsDeleted(val);
      formChanged();
    };
    return (
      <div>
        <Checkbox2 checked={IsDeleted} onChange={onChange} label="Is Deleted" />
      </div>
    );
  };

  return (
    <>
      {loading && <FormSpinner />}
      <Dialog toolbar onClose={close} title="Edit Attachment" actions={renderActions()}>
        <div className={isImage ? classes.imageDialogLayout : classes.g}>
          {isImage && attachmentBuffer && (
            <div className={classes.imagePreviewPane}>
              <img
                src={getFileURLFromBuffer(attachmentBuffer, FileType)}
                alt=""
                className={classes.imagePreview}
              />
            </div>
          )}
          <div className={isImage ? classes.detailsPane : ''}>
            <div className={classes.fileInfo}>
              <AttachmentIcon type={FileType} />
              {props.data.FileName}
            </div>
            {renderFileName()}
            {renderDescription()}
            {renderIsDeleted()}
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default connect(null, {
  closeEditAttachment,
  showSpinner,
  hideSpinner,
  handleError,
  notifyDataUpdate,
})(EditAttachment);
